/**
 * Configuración centralizada de la aplicación
 *
 * IMPORTANTE: Este archivo contiene la configuración específica de PyLC.
 * Para usar este proyecto con otra empresa, solo necesitas:
 * 1. Cambiar los valores en este archivo
 * 2. Reemplazar los assets en la carpeta correspondiente
 * 3. El resto del proyecto se adaptará automáticamente
 *
 * Este archivo contiene:
 * - Información de la empresa (PyLC específico)
 * - Colores y temas (PyLC específico) - FUENTE DE VERDAD para SCSS
 * - Logos y assets (PyLC específico)
 * - URLs y redirecciones (PyLC específico)
 * - Configuraciones generales (reutilizable)
 *
 * NOTA: Los colores definidos aquí se sincronizan automáticamente con
 * las variables SCSS a través de configHelpers.ts
 */

import JotformEmbed from '@/components/jotform/JotformEmbed.vue';
import { ProjectColorsConfig } from '@/types/config/project';

// ===========================================
// INFORMACIÓN DE LA EMPRESA
// ===========================================
export const companyConfig = {
    name: 'PyLC',
    fullName: 'Ejecutivo',
    description: 'Plataforma Ejecutiva de Seguros',
    tagline: 'Tu socio en seguros',
    version: '2.6.1', // Se sincroniza con package.json
    typeUserOne: 'Analista',
    typeUserTwo: 'Enlace',

    // URLs de redirección
    urls: {
        website: 'https://pylc.com',
        support: 'https://support.pylc.com',
        documentation: 'https://docs.pylc.com',
        terms: '/aviso-privacidad',
        privacy: '/aviso-privacidad'
    },

    // Información de contacto
    contact: {
        email: 'info@pylc.com',
        phone: '+52 55 1234 5678',
        address: 'Ciudad de México, México'
    }
};

// ===========================================
// LOGOS Y ASSETS
// ===========================================
export const assetsConfig = {
    logos: {
        main: '/assets/branding/logo_pylc.svg',
        icon: '/assets/branding/icon_pylc_c.svg',
        icon_dark: '/assets/branding/icon_pylc_w.svg',
        favicon: '/assets/branding/favicon_package_v5/favicon.svg',

        // Logos adicionales para diferentes contextos
        theme: '/assets/branding/favicon_package_v5/favicon.svg', // Si existe
        dark_theme: '/assets/branding/favicon_package_v5/favicon.svg', // Si existe
        theme_rtl: '/assets/branding/logo-light-rtl.svg',
        dark_theme_rtl: '/assets/branding/logo-dark-rtl.svg',
        small: '/assets/branding/favicon_package_v5/favicon.svg',

        // Iconos de folders
        icon_folder: '/assets/branding/icon_folder_pylc.svg'
    },

    // Favicons y PWA
    favicons: {
        appleTouchIcon: '/assets/branding/favicon_package_v5/apple-touch-icon.png',
        favicon32: '/assets/branding/favicon_package_v5/favicon-32x32.png',
        favicon16: '/assets/branding/favicon_package_v5/favicon-16x16.png',
        manifest: '/assets/branding/favicon_package_v5/site.webmanifest'
    },

    // Imágenes de fondo y decorativas
    images: {
        loginBackground: '', // Si existe
        dashboardBanner: '', // Si existe
        // Imagen de seguro por defecto (si no hay image_insurance en el item)
        defaultInsuranceImageLight: '/assets/branding/multi_company_arrows_clearmode.svg', // Si existe
        defaultInsuranceImageDark: '/assets/branding/multi_company_arrows_darkmode.svg' // Si existe
    },
    animations: {
        jotform: ''
    }
};

// ===========================================
// CONFIGURACIÓN DE TIPOGRAFÍA
// ===========================================
export const typographyConfig = {
    // Fuente principal de la aplicación
    fontFamily: 'Plus Jakarta Sans', // Nombre de la fuente
    // URL de Google Fonts (opcional, si la fuente está en Google Fonts)
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap',
    // Fuentes de respaldo (fallback)
    fallbackFonts: ['system-ui', 'Avenir', 'Helvetica', 'Arial', 'sans-serif'],
    // Si la fuente es de Google Fonts
    isGoogleFont: true
};

// ===========================================
// COLORES
// ===========================================

export const colorsConfig: ProjectColorsConfig = {
    light: {
        primary: '#511a6b', // Manteniendo el morado como color principal
        secondary: '#777777', // Gris como color secundario
        info: '#17a2b8', // Azul turquesa más estándar para información
        success: '#28a745', // Verde estándar para éxito
        warning: '#f8c076', // Amarillo estándar para advertencias
        error: '#dc3545', // Rojo estándar para errores
        errorSecondary: '#e57373', // Rojo más suave para errores secundarios
        indigo: '#6610f2', // Un tono más vibrante para el índigo
        lightprimary: '#f8f0fa', // Fondo más claro para "primary"
        lightinfo: '#e3f8fc', // Azul claro para "info"
        lightsecondary: '#f6f6f6', // Gris claro para fondo secundario
        lightsuccess: '#e6f9ef', // Verde claro para éxito
        lighterror: '#fde2e2', // Rojo claro para error
        lightwarning: '#fff7e6', // Amarillo claro para advertencias
        lightindigo: '#efe5fd', // Índigo claro para fondos
        textPrimary: '#212529', // Texto principal en negro intenso
        textSecondary: '#495057', // Texto secundario en gris oscuro
        borderColor: '#ced4da', // Gris claro para bordes
        inputBorder: '#dee2e6', // Gris más claro para bordes de inputs
        containerBg: '#fafafa', // Fondo de contenedores en blanco
        background: '#f8f9fa', // Fondo principal en gris claro
        hoverColor: '#e9ecef', // Color para hover en elementos
        surface: '#fafafa', // Superficies como blanco
        grey100: '#adb5bd', // Gris claro para texto terciario
        grey200: '#6c757d', // Gris oscuro para detalles secundarios
        darkbg: '#343a40', // Fondo oscuro en ciertos elementos
        bglight: '#f1f3f5', // Fondo claro para contrastes
        bgdark: '#e9ecef', // Fondo oscuro para sombras
        blueBin: '#0056b3', // Azul más oscuro para contraste en bin
        greenProject: '#008f39', // Verde más estándar para "Pylc"
        greenNewProject: '#00732c', // Verde más oscuro para nuevo
        greenRenovationProject: '#a2f19e', // Verde pastel para renovación
        orangeProject: '#ff8c42', // Naranja brillante
        yellowProject: '#fadb14', // Amarillo más vibrante
        pinkSecondaryProject: '#e91e63', // Rosa vibrante
        purpleComercialProject: '#6200ea' // Morado comercial vibrante
    },
    dark: {
        primary: '#9e5fbf',
        secondary: '#afafaf',
        info: '#17a2b8', // Azul turquesa más estándar para información
        success: '#28a745', // Verde estándar para éxito
        warning: '#F8B15D', // Amarillo estándar para advertencias
        error: '#dc3545', // Rojo estándar para errores
        errorSecondary: '#e57373', // Rojo más suave para errores secundarios
        indigo: '#8763DA',
        lightprimary: '#f4e9fa',
        lightinfo: '#1a3247',
        lightsecondary: '#2f3f4c',
        lightsuccess: '#102d35',
        lighterror: '#72483d',
        lightwarning: '#282b32',
        textPrimary: '#EAEFF4',
        textSecondary: '#fff',
        lightindigo: '#1d233e',
        borderColor: '#182D41',
        inputBorder: '#465670',
        containerBg: '#131526',
        background: '#2f2f40',
        hoverColor: '#333f55',
        surface: '#131526',
        grey100: '#7C8FAC',
        grey200: '#EAEFF4',
        darkbg: '#111936',
        bglight: '#16293e',
        bgdark: '#16293e',
        blueBin: '#0038ff',
        greenProject: '#0bc707',
        greenNewProject: '#078f04',
        greenRenovationProject: '#c1ff72',
        orangeProject: '#ff914d',
        yellowProject: '#ffdd4d',
        pinkSecondaryProject: '#ff004d',
        purpleComercialProject: '#5E17EB'
    },
    loginColors: {
        left: '#755dd9',
        right: '#511A6B'
    }
};

export const config = {
    company: companyConfig,
    assets: assetsConfig,
    colors: colorsConfig,
    typography: typographyConfig
};

// Exportación por defecto
export default config;
